import { ReactNode } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRotateLeft,
  faBook,
  faBuilding,
  faShieldCheck,
  faStar,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ButtonLink from '@v2/shared/components/ButtonLink';
import ContentCard from '@v2/shared/components/ContentCard';
import FaqAccordion from '@v2/shared/components/FaqAccordion';
import PageHeading from '@v2/shared/components/PageHeading';
import { cn } from '@v2/shared/lib/utils';
import { useSite } from '@v2/shared/lib/site';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * CJ's publisher signup for advertiser 3035494, linked from the hero and the closing card.
 *
 * This is the mockup's url, and it is where the old one already went. The page used to
 * point at `signup.cj.com/member/brandedPublisherSignUp.do?air_refmerchantid=3035494`,
 * which redirects here in three hops, carrying the advertiser id across the rename to
 * `advertiserId` — followed with `curl -L` on 2026-09-12, final url identical to this
 * string, 200. So the destination is unchanged and the hops are gone. The advertiser id
 * is the attribution: if it ever has to change, it changes in both halves of that chain.
 */
const CJ_SIGNUP_URL = 'https://public.cj.com/signup/publisher?advertiserId=3035494';

const AFFILIATE_EMAIL = 'affiliates@discountmags.com';

/**
 * The anchors, in page order, as id to the words both navigations use for them.
 *
 * One list rather than two, so a jump pill cannot point at an anchor nothing carries —
 * the failure that stays invisible until someone clicks. `compare` is the odd one out: it
 * is a heading inside the commission card rather than a card of its own, because the
 * comparison only means anything next to the rates it is comparing.
 */
const SECTIONS = {
  rates: 'Commission',
  compare: 'How we compare',
  why: 'Why it converts',
  how: 'How it works',
  who: 'Who we work with',
  faqs: 'FAQs',
  apply: 'Apply',
} as const;

type SectionId = keyof typeof SECTIONS;

/**
 * The one way this file is allowed to name an anchor, on the section that carries it and
 * on every link that points at it. Both sides go through here, so a typo on either is a
 * compile error rather than a pill that silently does nothing when clicked.
 *
 * What it does not catch: a key added to `SECTIONS` that no section ever carries. Nothing
 * in the type system ties the map to the JSX below, so that stays a reading job.
 *
 * A function rather than `satisfies` (which /shipping uses)
 * only because prettier is pinned at 2.7.1 and cannot parse a file containing one.
 */
const anchor = (id: SectionId) => id;
const anchorHref = (id: SectionId) => `#${id}`;

/** The four numbers a publisher is deciding on, above the section that gives them in full. */
const GLANCE: { figure: string; detail: ReactNode }[] = [
  {
    figure: 'Up to 18%',
    detail: (
      <>
        on <b>print magazine</b> subscriptions
      </>
    ),
  },
  {
    figure: '10%',
    detail: (
      <>
        on <b>digital magazine</b> subscriptions
      </>
    ),
  },
  {
    figure: '5%',
    detail: (
      <>
        on <b>books, calendars</b> and print goods
      </>
    ),
  },
  {
    figure: '60 days',
    detail: (
      <>
        to earn on a referral, <b>from first click</b>
      </>
    ),
  },
];

/** The commission table. */
const RATES = [
  {
    category: 'Print magazines',
    rate: 'Up to 18%',
    included:
      'Most print titles earn the full 18%. A small number of low margin titles pay less, and CJ shows the exact rate for every item group.',
  },
  {
    category: 'Digital magazines',
    rate: '10%',
    included: 'Digital editions delivered through Zinio and Magzter.',
  },
  {
    category: 'Books & print goods',
    rate: '5%',
    included: 'Books, calendars and the rest of our print catalog.',
  },
];

/** The comparison table. `isUs` tints the row that is this program. */
const COMPARISON = [
  {
    seller: 'DiscountMags',
    earns: 'Up to 18% print, 10% digital, 5% books',
    window: '60 days',
    isUs: true,
  },
  { seller: 'Magazines.com', earns: '8%', window: '30 days' },
  {
    seller: 'Direct from publisher',
    earns:
      'One program per publisher, where one exists at all, each with its own application, rate and terms',
    window: 'Varies',
  },
  {
    seller: 'Amazon',
    earns: 'Nothing. Amazon stopped selling magazine subscriptions in 2023.',
    window: 'n/a',
  },
];

/** The four steps, all of which happen inside CJ. */
const STEPS = [
  {
    title: 'Apply',
    detail:
      "Join CJ if you're not already a publisher there, then apply to the DiscountMags program.",
  },
  {
    title: 'Get approved',
    detail: 'We review applications by hand and look at what you publish and who reads it.',
  },
  {
    title: 'Place links',
    detail: 'Deep link to any title or category, use our banners, or run our current promotions.',
  },
  {
    title: 'Get paid',
    detail:
      'CJ tracks every referral, reports it in real time, and pays you on their monthly schedule.',
  },
];

const GOOD_FIT = [
  'Content sites and blogs with a reading audience',
  'Niche and hobby communities, forums and newsletters',
  'Gift guides, seasonal roundups and deal editorial',
  'Schools, libraries and nonprofit fundraising pages',
];

const NOT_A_FIT = [
  'Trademark bidding on our brand or the titles we sell',
  'Toolbars, adware, cookie stuffing or forced clicks',
  'Unsolicited email or SMS promoting our links',
  'Cash back and rebate sites',
  'Browser extensions that apply coupon codes at checkout',
  'Sites built on adult, hateful or illegal content',
];

/**
 * Headings, sized once rather than at every call site.
 *
 * Margins are longhands on purpose: `prose` compiles to `margin-top: ... !important`
 * inside `.v2-root`, so the `m-0` shorthand silently loses to it while `mt-0` wins. See
 * `postcss/beatLegacyImportant.js`, and /refund which hit the same thing.
 *
 * Compose these with `twMerge`, never `cn`. A call site that overrides one of the margins
 * baked in here is a real conflict, and clsx settles it by emitting both classes and
 * letting the compiled sheet decide — which is stylesheet order, not intent. Measured:
 * `cn(H3, 'mt-0')` rendered at `margin-top: 24px`, because Tailwind emits `.mt-6` after
 * `.mt-0` and both are `!important` at equal specificity. The heading looked untouched
 * and was silently 24px out.
 */
const H2 = 'mb-0 mt-0 text-2xl font-extrabold tracking-tight text-slate-900';
const H3 = 'mb-1.5 mt-6 text-xl font-extrabold text-slate-900';
const TAGLINE = 'mb-4 mt-1 text-sm text-slate-500';

/** A muted caps label, over a figure or a column. */
const MICRO_LABEL = 'text-[11px] font-bold uppercase tracking-wider';

/**
 * What an anchored section clears to land below the fixed header, which is three heights
 * and the tallest is the middle one: 53px on a phone, 126px from md to xl where the
 * search bar has its own row, and 82px at xl once it moves up beside the nav.
 */
const ANCHOR_OFFSET = 'scroll-mt-14 md:scroll-mt-32 xl:scroll-mt-24';

/** The chart cells, as /refund-policy and /shipping draw them. */
const CELL = 'border-0 border-b border-solid border-slate-200 px-3.5 py-3 text-left align-top';
const HEADER_CELL = cn(CELL, 'bg-slate-50 text-slate-500', MICRO_LABEL);

/** The hero's two buttons, which sit on the gradient and so cannot be the filled red one. */
const HERO_PRIMARY = 'bg-white text-primary-700 hover:bg-primary-50 normal-case tracking-normal';
const HERO_GHOST = cn(
  'border-2 border-solid border-white/70 bg-transparent text-white',
  'hover:border-white hover:bg-white/10 normal-case tracking-normal'
);

/** The outlined counterpart of the filled button, as the refund page draws it. */
const GHOST_BUTTON = cn(
  'border border-solid border-primary bg-white text-primary',
  'hover:bg-primary-50 normal-case tracking-normal'
);

/** A tinted panel for an aside that is still running copy — the mockup's `.note`. */
function Note({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'rounded-lg border border-solid border-slate-200 bg-slate-50 px-4 py-3.5 text-sm',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * A table that scrolls rather than squeezing, and the wrapper is what scrolls:
 * `display:block` on the table itself is the usual shortcut and costs it its table
 * semantics in some screen readers. It takes a `tabIndex` because a region that scrolls
 * has to scroll without a mouse, and the table a floor, or the columns keep shrinking and
 * there is nothing left to scroll.
 *
 * `label` is not optional, and the tabIndex is why: a focusable element with no accessible
 * name is a stop a keyboard user lands on with nothing announced, and this page has two of
 * them. `role="region"` is what makes the name get read.
 *
 * `minWidth` because the two tables do not have the same floor — the mockup gives the
 * comparison table 560px against the rate table's 520px, and it needs them: three columns,
 * one holding a full sentence.
 */
function Chart({
  label,
  minWidth = 'min-w-[520px]',
  children,
}: {
  label: string;
  minWidth?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="not-prose my-4 w-full overflow-x-auto"
      tabIndex={0}
      role="region"
      aria-label={label}
    >
      <table
        className={cn(
          minWidth,
          'w-full border-collapse text-left text-sm text-slate-700',
          '[&_tr:last-child_td]:border-b-0'
        )}
      >
        {children}
      </table>
    </div>
  );
}

/**
 * /affiliate-program.
 *
 * The 2026-09-12 rebuild, against the mockup on the task. The page it replaces was a
 * single card of prose that stated one flat 18% rate, named an agency that no longer
 * manages the program, and offered a phone and fax number for a team that answers over
 * CJ and email. Every rate is now on the page per category, next to the 60 day window and
 * the terms that decide whether an applicant is a fit — which is the set of questions an
 * affiliate applies or does not apply on.
 *
 * Copy is the content owner's and came with the mockup: it may not be re-worded,
 * re-ordered or re-punctuated without a task that supplies the new wording. That includes
 * the competitor rates in the comparison table and the date the note under it was
 * checked.
 *
 * The design is the one /refund-policy and /shipping
 * already wear — `PageHeading`'s band, the strip of figures pulled up over it, the jump
 * pills, `ContentCard` per section and `FaqAccordion` for the questions — so the mockup's
 * own stylesheet is not carried over. Each card owns the anchor both navigations jump to.
 *
 * Nothing here varies by visitor, which is what lets the template cache the render for a
 * day. Keep it that way.
 */
export default function AffiliateProgram() {
  const { buildUrl } = useUrlGenerator();
  const { country } = useSite();

  // Hand-written paths, so they go through buildUrl: the AU storefront serves from a base
  // directory and a bare path drops its visitors onto the .com site.
  const guaranteeUrl = buildUrl('/guarantee');
  const bestsellersUrl = buildUrl('/bestsellers');
  const refundPolicyUrl = buildUrl('/refund-policy');
  const reviewsUrl = buildUrl('/reviews');
  const bulkOrderUrl = buildUrl('/bulk-order');
  const contactUrl = buildUrl('/contact');

  /** The four reasons the traffic converts, each closing on the page that owns the claim. */
  const REASONS: { icon: IconProp; title: string; body: ReactNode; href: string; cta: string }[] = [
    {
      icon: faShieldCheck as IconProp,
      title: 'The lowest price publishers allow',
      body: 'Our prices are the lowest the publisher permits, and if someone finds the same subscription cheaper from an authorized seller, we match it. Price shoppers who click through tend to stop shopping.',
      href: guaranteeUrl,
      cta: 'See the guarantee',
    },
    {
      icon: faBook as IconProp,
      title: '1,000,000+ titles to link to',
      body: 'Magazines, books, calendars and print goods, which means a relevant deep link for almost any niche: cooking, quilting, fishing, motorsport, kids, faith, trade titles.',
      href: bestsellersUrl,
      cta: 'Browse the catalog',
    },
    {
      icon: faArrowRotateLeft as IconProp,
      title: 'Orders that stick',
      body: 'Customers get six months to cancel for a pro-rated refund, so they buy without hesitating. A clear refund policy up front means fewer disputes later.',
      href: refundPolicyUrl,
      cta: 'Read the refund policy',
    },
    {
      icon: faStar as IconProp,
      title: 'Reviews you can point at',
      body: "Our reviews are collected and verified independently by SmartCustomer from confirmed buyers. We can't edit or remove them, so you're sending readers somewhere they can check for themselves.",
      href: reviewsUrl,
      cta: 'See the reviews',
    },
  ];

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Affiliate Program' }]} />

      {/* The band every institutional page wears, rather than a hero this page rolls
          itself. The buttons go in `actions` and not in `intro`: `intro` is a paragraph
          at 90% opacity, and opacity composites its whole subtree, so a white button
          inside it paints pink over the gradient. */}
      <PageHeading
        eyebrow="DiscountMags Affiliate Program"
        // A deeper floor so the strip below can sit over the band's bottom edge without
        // touching the buttons. Measured: on the band's own `md:py-6` the strip's top edge
        // met the bottom of "Apply through CJ" exactly, at every width from md up. 40px
        // against the strip's -24px pull leaves the ~14px the mockup has.
        //
        // `md:pb-10` is not a duplicate of `pb-10`: PageHeading's floor is `md:py-6`, and
        // Tailwind emits every responsive variant after the whole base layer, so a base
        // `pb-10` loses to it from md up. Overriding a `md:` padding takes a `md:` padding.
        // Same reason /shipping writes `pb-16 md:pb-16`.
        className="pb-10 md:pb-10"
        intro="Your audience already reads something. We sell magazine subscriptions, books and print goods at the lowest prices publishers allow, with a guarantee behind every order. Link to us, and you earn on what they buy for the next 60 days, up to 18% per order. Tracking and payments run through CJ."
        actions={
          <>
            <ButtonLink
              href={CJ_SIGNUP_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={HERO_PRIMARY}
            >
              Apply through CJ
            </ButtonLink>
            <ButtonLink href={anchorHref('how')} className={HERO_GHOST}>
              See how it works
            </ButtonLink>
          </>
        }
      >
        Send readers our way, earn up to 18% on what they order
      </PageHeading>

      {/* The figures, pulled up over the band the way /refund pulls its up.
          `relative` is load-bearing rather than decoration: PageHeading is a positioned
          element, so a static strip pulled over it is painted behind it and every cell
          loses its top rows. */}
      <ul
        className={cn(
          'relative z-10 mx-3 -mt-6 grid list-none grid-cols-2 p-0 md:mx-5 md:grid-cols-4',
          'overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm'
        )}
      >
        {GLANCE.map((item, index) => (
          <li
            key={item.figure}
            className={cn(
              'border-0 border-solid border-slate-200',
              // Dividers between cells: two columns on a phone, four above it.
              index % 2 === 1 && 'border-l',
              index >= 2 && 'border-t md:border-t-0',
              index > 0 && 'md:border-l'
            )}
          >
            <a
              href={anchorHref('rates')}
              className="block h-full px-4 py-3.5 no-underline hover:bg-slate-50"
            >
              <span className="block text-lg font-extrabold tabular-nums leading-tight text-slate-900">
                {item.figure}
              </span>
              <span className="mt-1 block text-xs leading-snug text-slate-500 [&>b]:text-primary">
                {item.detail}
              </span>
            </a>
          </li>
        ))}
      </ul>

      <nav aria-label="On this page" className="mt-6 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold text-slate-500">On this page:</span>
        {Object.entries(SECTIONS).map(([id, label]) => (
          <a
            key={id}
            href={`#${id}`}
            className={cn(
              'rounded-full border border-solid border-slate-200 px-3.5 py-1',
              'font-semibold text-slate-700 no-underline',
              'hover:border-primary hover:text-primary'
            )}
          >
            {label}
          </a>
        ))}
      </nav>

      <div className="mt-5 flex flex-col gap-6">
        <ContentCard id={anchor('rates')} className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>What you earn</h2>
          <p className={TAGLINE}>Paid on the order total, per category, through CJ.</p>

          <Chart label="Commission rates by category">
            <thead>
              <tr>
                <th scope="col" className={HEADER_CELL}>
                  Category
                </th>
                <th scope="col" className={HEADER_CELL}>
                  Commission
                </th>
                <th scope="col" className={HEADER_CELL}>
                  What&apos;s included
                </th>
              </tr>
            </thead>
            <tbody>
              {RATES.map((row) => (
                <tr key={row.category}>
                  <td className={cn(CELL, 'whitespace-nowrap font-bold text-slate-900')}>
                    {row.category}
                  </td>
                  <td
                    className={cn(
                      CELL,
                      'whitespace-nowrap text-base font-extrabold tabular-nums text-primary'
                    )}
                  >
                    {row.rate}
                  </td>
                  <td className={cn(CELL, 'text-slate-500')}>{row.included}</td>
                </tr>
              ))}
            </tbody>
          </Chart>

          <Note>
            <p className="mb-0 mt-0">
              <b className="text-slate-900">Where the exact rate lives.</b> Rates are set per item
              group in CJ, so the number you see there is always the number that pays. The rates
              above are what the large majority of orders earn.
            </p>
          </Note>

          <Note className="mt-3.5">
            <p className="mb-0 mt-0">
              <b className="text-slate-900">The 60 day window.</b> Someone clicks your link today
              and orders seven weeks later, you still earn. CJ tracks the referral for 60 days and
              reports it in real time, and CJ pays you on their standard monthly schedule.
            </p>
          </Note>

          {/* The comparison is a heading inside this card rather than a card of its own,
              and still carries an anchor because a jump pill points at it. */}
          <h3 id={anchor('compare')} className={twMerge(H3, ANCHOR_OFFSET)}>
            How that compares
          </h3>

          {/* 560px rather than the default 520px: the mockup gives this table the wider
              floor, and it needs it — three columns, one holding a full sentence. */}
          <Chart
            label="How DiscountMags compares to other magazine sellers"
            minWidth="min-w-[560px]"
          >
            <thead>
              <tr>
                <th scope="col" className={HEADER_CELL}>
                  Where your readers buy magazines
                </th>
                <th scope="col" className={HEADER_CELL}>
                  What the affiliate earns
                </th>
                <th scope="col" className={HEADER_CELL}>
                  Referral window
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.seller} className={cn(row.isUs && 'bg-primary-50')}>
                  <td
                    className={cn(
                      CELL,
                      'whitespace-nowrap font-bold',
                      row.isUs ? 'text-primary' : 'text-slate-900'
                    )}
                  >
                    {row.seller}
                  </td>
                  <td className={cn(CELL, row.isUs && 'text-slate-900')}>{row.earns}</td>
                  <td className={cn(CELL, 'tabular-nums', row.isUs && 'text-slate-900')}>
                    {row.window}
                  </td>
                </tr>
              ))}
            </tbody>
          </Chart>

          <p className="mb-0 mt-2.5 text-xs leading-relaxed text-slate-500">
            One application here covers the whole catalog. Going direct from publisher means a
            separate program, a separate approval and a separate payout for each family of titles
            you want to link to. Competitor terms as published on their affiliate network listings,
            checked September 2026. Any program can change its rates at any time, so check before
            you compare.
          </p>
        </ContentCard>

        <ContentCard id={anchor('why')} className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>Why these links convert</h2>
          <p className={TAGLINE}>What your audience gets when they land on us.</p>

          {/* `not-prose`: these are tiles, and prose would restyle the headings and the
              closing link inside each of them. */}
          <div className="not-prose grid gap-4 md:grid-cols-2">
            {REASONS.map((reason) => (
              <div
                key={reason.title}
                className="flex flex-col rounded-lg border border-solid border-slate-200 p-5"
              >
                <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary">
                  <FontAwesomeIcon icon={reason.icon} aria-hidden />
                </span>
                <h3 className="mb-1.5 mt-0 text-lg font-extrabold text-slate-900">
                  {reason.title}
                </h3>
                <p className="mb-4 mt-0 text-sm text-slate-700">{reason.body}</p>
                <a
                  href={reason.href}
                  className="mt-auto text-sm font-bold text-primary no-underline hover:underline"
                >
                  {reason.cta} <span aria-hidden>&rarr;</span>
                </a>
              </div>
            ))}
          </div>
        </ContentCard>

        <ContentCard id={anchor('how')} className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>How it works</h2>
          <p className={TAGLINE}>Four steps, all of it inside CJ.</p>

          {/* An ordered list, because the steps are a sequence. `not-prose` because the
              numbers are drawn rather than listed, and prose would style its own counters
              underneath these. */}
          <ol className="not-prose m-0 grid list-none gap-3.5 p-0 md:grid-cols-4">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className="rounded-lg border border-solid border-slate-200 bg-slate-50 p-4"
              >
                <span
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full',
                    'bg-primary text-sm font-extrabold tabular-nums text-white'
                  )}
                >
                  {index + 1}
                </span>
                <p className="mb-0 mt-2.5 font-extrabold text-slate-900">{step.title}</p>
                <p className="mb-0 mt-1 text-sm leading-snug text-slate-500">{step.detail}</p>
              </li>
            ))}
          </ol>
        </ContentCard>

        <ContentCard id={anchor('who')} className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>Who we work with</h2>

          <div className="mt-4 grid gap-6 md:grid-cols-2">
            <div>
              <h3 className={twMerge(H3, 'mt-0')}>A good fit</h3>
              <ul className="mb-0 ml-0 list-disc pl-5 text-sm text-slate-700 marker:text-primary">
                {GOOD_FIT.map((item) => (
                  <li key={item} className="mb-2 last:mb-0">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className={twMerge(H3, 'mt-0')}>Not a fit</h3>
              <ul className="mb-0 ml-0 list-disc pl-5 text-sm text-slate-700 marker:text-primary">
                {NOT_A_FIT.map((item) => (
                  <li key={item} className="mb-2 last:mb-0">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Note className="mt-5">
            <p className="mb-0 mt-0">
              <b className="text-slate-900">Coupon sites:</b> we work with a small number of them
              and review each one individually, so apply and tell us how you source codes. We
              don&apos;t accept cash back sites or extensions that inject codes at checkout, because
              they take credit for a sale the customer was already making.
            </p>
          </Note>

          <Note className="mt-3.5">
            <p className="mb-0 mt-0">
              The full program terms live in CJ and are what govern. If you&apos;re not sure whether
              something is allowed, ask us before you run it.
            </p>
          </Note>
        </ContentCard>

        {/* No `prose` on this one: the accordion is UI, not running copy, and prose would
            restyle its button and its panel. The answers carry their own link colour. */}
        <ContentCard id={anchor('faqs')} className={ANCHOR_OFFSET}>
          <h2 className={twMerge(H2, 'mb-4')}>Affiliate FAQs</h2>

          <div className="grid gap-2.5">
            <FaqAccordion
              defaultOpen
              question="Which network do you use?"
              answer={
                <>
                  CJ (formerly Commission Junction) handles tracking, reporting and payment.
                  You&apos;ll need a CJ publisher account, which is free, and you manage everything
                  from there.
                </>
              }
            />
            <FaqAccordion
              question="Why does the rate differ between titles?"
              answer={
                <>
                  Our margin isn&apos;t the same on every title. Most print magazines earn the full
                  18%, and a small number of low margin titles earn less. Rates are set per item
                  group in CJ, so you can always see exactly what a title pays before you link to
                  it.
                </>
              }
            />
            <FaqAccordion
              question="How long does the referral last?"
              answer={
                <>
                  60 days from the click. If the person you referred orders any time in that window,
                  the sale is credited to you.
                </>
              }
            />
            <FaqAccordion
              question="Do renewals earn commission?"
              answer={
                <>
                  Commission is paid on the order your link produced. Later renewals are not
                  commissioned separately.
                </>
              }
            />
            <FaqAccordion
              question="What happens if an order is refunded?"
              answer={
                <>
                  If a customer cancels and is refunded, the commission on that order is reversed in
                  CJ, the same way it works with every program there.
                </>
              }
            />
            <FaqAccordion
              question="Can I link to a specific magazine?"
              answer={
                <>
                  Yes. Deep link to any title, category or promotion page and the link tracks the
                  same as a homepage link. Titles and categories are all linkable from{' '}
                  <a href={bestsellersUrl} className="text-primary underline">
                    our catalog
                  </a>
                  .
                </>
              }
            />
            <FaqAccordion
              question="Who do I talk to?"
              answer={
                <>
                  Message us through CJ, which keeps the conversation attached to your publisher
                  account, or email{' '}
                  <a href={`mailto:${AFFILIATE_EMAIL}`} className="text-primary underline">
                    {AFFILIATE_EMAIL}
                  </a>
                  . We answer affiliate email Monday through Friday.
                </>
              }
            />
          </div>
        </ContentCard>

        <ContentCard id={anchor('apply')} className={cn('prose', ANCHOR_OFFSET)}>
          <h2 className={H2}>Ready to start?</h2>
          <p className={TAGLINE}>
            Applications are reviewed by our team. Free to join, nothing to maintain.
          </p>

          <div className="not-prose flex flex-wrap gap-3">
            <ButtonLink
              href={CJ_SIGNUP_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="normal-case tracking-normal"
            >
              Apply through CJ
            </ButtonLink>
            <ButtonLink href={`mailto:${AFFILIATE_EMAIL}`} className={GHOST_BUTTON}>
              Email the affiliate team
            </ButtonLink>
          </div>

          <p className="mb-0 mt-4 text-sm text-slate-500">
            Already in the program? Sign in at <b className="text-slate-900">CJ</b> to grab links
            and check your reporting.
          </p>
        </ContentCard>

        {/* The closing aside, tinted rather than boxed so it reads as the end of the page
            rather than as another section. Same treatment the refund page's closes with. */}
        <div
          className={cn(
            'flex items-start gap-3.5 rounded-lg border border-solid p-5',
            'border-primary-100 bg-primary-50'
          )}
        >
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-primary text-white">
            <FontAwesomeIcon icon={faBuilding as IconProp} aria-hidden />
          </span>
          <div>
            <p className="m-0 font-bold text-slate-900">
              Buying for a school, office or waiting room instead?
            </p>
            <p className="m-0 mt-1 text-sm text-slate-700">
              That&apos;s not an affiliate question.{' '}
              {/* Bulk orders is a US-only destination — `contact_bulkorder.html` has no CA or
                  AU override, so the link 404s on those storefronts, and this page is served
                  on all three. Gated the way the footer and the account menu gate it rather
                  than pointed at a dead page. The mockup is the US wording and is what US
                  renders; elsewhere the sentence keeps the half that still resolves. */}
              {country === 'US' ? (
                <>
                  See{' '}
                  <a href={bulkOrderUrl} className="text-primary underline">
                    bulk orders
                  </a>
                  , or{' '}
                </>
              ) : (
                <>Please </>
              )}
              <a href={contactUrl} className="text-primary underline">
                contact customer care
              </a>{' '}
              for help with an existing order.
            </p>
          </div>
        </div>

        <p className="m-0 text-xs leading-relaxed text-slate-500">
          This site is not officially affiliated with, associated with, or endorsed by any magazine
          or publisher. Magazine names, logos, and images may be registered trademarks of individual
          magazines, publishers, or parent companies.
        </p>
      </div>
    </>
  );
}

AffiliateProgram.layoutName = 'Layouts.Institutional';
